import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import HeroSection from '../home/HeroSection';
import ServicesSection from '../home/ServicesSection';
import GallerySection from '../home/GallerySection';
import PricingSection from '../home/PricingSection';
import ValuesSection from '../home/ValuesSection';
import ContactSection from '../home/ContactSection';
import {
  ICON_MAP, TEXT_SIZE_PRESETS, HEADING_SIZE_PRESETS,
} from './blockDefs';

const alignClass = (a) => (a === 'left' ? 'text-left' : a === 'right' ? 'text-right' : 'text-center');
const flexAlign = (a) => (a === 'left' ? 'justify-start' : a === 'right' ? 'justify-end' : 'justify-center');

// Renders a single block. `ctx` carries shared runtime data (pricing, gallery, quote dialog).
export default function BlockRenderer({ block, ctx = {} }) {
  if (!block) return null;
  const c = block.content || {};
  const s = block.settings || {};

  switch (block.type) {
    case 'hero':
      return (
        <HeroSection
          content={c}
          quoteDialogOpen={ctx.quoteDialogOpen}
          setQuoteDialogOpen={ctx.setQuoteDialogOpen}
        />
      );
    case 'services':
      return <ServicesSection services={c.items || []} fullIconMap={ICON_MAP} heading={c.heading} subheading={c.subheading} />;
    case 'gallery_preview':
      return <GallerySection galleryItems={ctx.galleryItems || []} heading={c.heading} subheading={c.subheading} />;
    case 'pricing':
      return (
        <PricingSection
          pricingCategories={ctx.pricingCategories || []}
          quoteDialogOpen={ctx.quoteDialogOpen}
          setQuoteDialogOpen={ctx.setQuoteDialogOpen}
          heading={c.heading}
          subheading={c.subheading}
        />
      );
    case 'values':
      return <ValuesSection values={c.items || []} iconMap={ICON_MAP} heading={c.heading} columns={s.columns || 2} />;
    case 'contact':
      return <ContactSection />;
    case 'image_text':
      return <ImageTextBlock content={c} />;
    case 'heading':
      return (
        <h2 className={`font-bold ${HEADING_SIZE_PRESETS[c.size] || HEADING_SIZE_PRESETS.large} ${alignClass(c.align)}`} style={{ color: c.color || '#ffffff' }}>
          {c.text}
        </h2>
      );
    case 'text':
      return (
        <p className={`leading-relaxed whitespace-pre-line ${TEXT_SIZE_PRESETS[c.size] || TEXT_SIZE_PRESETS.normal} ${alignClass(c.align)}`} style={{ color: c.color || '#e2e8f0' }}>
          {c.text}
        </p>
      );
    case 'image': {
      const img = c.url ? (
        <img src={c.url} alt={c.alt || ''} className="rounded-lg inline-block h-auto object-cover" style={{ width: `${c.width || 100}%` }} />
      ) : (
        <div className="rounded-lg border-2 border-dashed border-slate-600 text-slate-500 text-sm py-16 px-6" style={{ width: `${c.width || 100}%` }}>No image selected</div>
      );
      return (
        <div className={`flex ${flexAlign(c.align)}`}>
          {c.link ? <a href={c.link} className="inline-block" style={{ width: `${c.width || 100}%` }}>{img}</a> : img}
        </div>
      );
    }
    case 'button': {
      const cls = c.style === 'outline'
        ? 'border-2 border-red-600 text-white hover:bg-red-600'
        : 'bg-red-600 hover:bg-red-700 text-white';
      const sizeCls = c.size === 'large' ? 'px-10 py-5 text-lg' : c.size === 'small' ? 'px-5 py-2.5 text-sm' : 'px-8 py-4 text-base';
      const isRoute = (c.link || '').startsWith('/') && !(c.link || '').startsWith('/#');
      const btn = <span className={`inline-flex items-center rounded-md font-semibold transition-colors ${cls} ${sizeCls}`}>{c.text}<ChevronRight className="w-5 h-5 ml-2" /></span>;
      return (
        <div className={`flex ${flexAlign(c.align)}`}>
          {isRoute ? <Link to={c.link}>{btn}</Link> : <a href={c.link || '#'}>{btn}</a>}
        </div>
      );
    }
    case 'cards':
      return <CardsBlock content={c} settings={s} />;
    case 'cta':
      return (
        <div className="rounded-2xl bg-gradient-to-br from-red-600 to-red-700 p-10 md:p-14 text-center">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3">{c.heading}</h3>
          {c.text && <p className="text-red-50 text-lg max-w-2xl mx-auto mb-6">{c.text}</p>}
          {c.button_text && (
            <a href={c.button_link || '#'} className="inline-flex items-center rounded-md bg-white text-red-700 hover:bg-red-50 px-8 py-4 font-semibold transition-colors">
              {c.button_text}<ChevronRight className="w-5 h-5 ml-2" />
            </a>
          )}
        </div>
      );
    case 'gallery':
      return <CustomGalleryBlock content={c} />;
    case 'divider':
      return <hr className="border-slate-700" />;
    case 'spacer':
      return <div style={{ height: c.size === 'large' ? 96 : c.size === 'small' ? 24 : 48 }} />;
    default:
      return null;
  }
}

const ImageTextBlock = ({ content: c }) => {
  const imageRight = c.image_pos === 'right';
  const Img = (
    <div className={`flex flex-col items-center ${imageRight ? 'order-2 lg:order-2' : 'order-2 lg:order-1'}`}>
      {c.image_url ? (
        <div className="relative rounded-lg overflow-hidden border-2 border-gray-400/20 max-w-md">
          <img src={c.image_url} alt={c.image_caption || c.heading || ''} className="w-full h-auto object-cover" />
        </div>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-slate-600 text-slate-500 text-sm py-24 px-10 max-w-md w-full text-center">No image selected</div>
      )}
      {c.image_caption && <p className="text-center text-gray-400 text-sm mt-4 italic">{c.image_caption}</p>}
    </div>
  );
  const Text = (
    <div className={imageRight ? 'order-1 lg:order-1' : 'order-1 lg:order-2'}>
      <div className="space-y-4 text-white leading-relaxed">
        {(c.paragraphs || []).map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
  return (
    <section id="about" className="py-20 bg-slate-950/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(c.heading || c.subheading) && (
          <div className="text-center mb-12">
            {c.heading && <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">{c.heading}</h2>}
            {c.subheading && <p className="text-xl text-gray-300 font-semibold">{c.subheading}</p>}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {imageRight ? <>{Text}{Img}</> : <>{Img}{Text}</>}
        </div>
      </div>
    </section>
  );
};

const CardsBlock = ({ content: c, settings: s }) => {
  const cols = s.columns === 3 ? 'md:grid-cols-3' : s.columns === 1 ? 'md:grid-cols-1' : 'md:grid-cols-2';
  return (
    <div>
      {c.heading && <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 text-center">{c.heading}</h2>}
      <div className={`grid grid-cols-1 ${cols} gap-6 max-w-5xl mx-auto`}>
        {(c.items || []).map((item) => {
          const Icon = ICON_MAP[item.icon] || ICON_MAP.shield;
          return (
            <div key={item.id} className="bg-slate-800 border border-slate-700 rounded-lg p-6 hover:border-gray-400/50 transition-all">
              <div className="w-12 h-12 bg-red-600/10 rounded-lg flex items-center justify-center mb-4">
                <Icon className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-white text-xl font-semibold mb-2">{item.title}</h3>
              <p className="text-gray-300 leading-relaxed">{item.description}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CustomGalleryBlock = ({ content: c }) => (
  <div>
    {c.heading && <h2 className="text-4xl md:text-5xl font-bold text-white mb-10 text-center">{c.heading}</h2>}
    {(c.items || []).length === 0 ? (
      <div className="text-center text-slate-500 text-sm py-10 border-2 border-dashed border-slate-700 rounded-lg">No images added yet</div>
    ) : (
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {c.items.map((it, i) => (
          <figure key={i} className="rounded-lg overflow-hidden border border-slate-700">
            <img src={it.url} alt={it.caption || ''} className="w-full h-56 object-cover" />
            {it.caption && <figcaption className="text-xs text-gray-400 p-2">{it.caption}</figcaption>}
          </figure>
        ))}
      </div>
    )}
  </div>
);
